import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import useDropdown from 'Hooks/useDropdown';

import DotsIcon from 'Assets/icons/dots-horizontal.svg';

/**
 * Overflow menu for a table row's secondary actions. Keep a row's primary loop
 * inline in its Actions cell — reconciliation keeps selection, confirm and
 * cancel out there — and move everything else in here so the column stops being
 * eight icons wide.
 *
 * The panel is portalled only while the menu is open: on a 40-row page, keeping a
 * hidden one per row would leave 40+ parked in <body>. `useDropdown`'s reposition
 * and outside-click effects are both keyed on `isOpen`, so they pick the node up
 * on the render that opens it. Its reposition logic is worth keeping — it closes
 * the menu when the trigger scrolls out of the table's horizontally scrollable
 * wrapper instead of floating over unrelated content.
 *
 * `actions` is a list of `{ key, label, icon, onClick, disabled }`; falsy entries
 * are dropped so callers can gate an action inline. The trigger stops its own
 * click, so it stays safe in a row that opens an editor on click.
 */
export default function RowActionsMenu({ actions }) {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, close]);

    const items = (actions ?? []).filter(Boolean);

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={$t('More actions')}
                title={$t('More actions')}
                // Every cell in the row opens the row editor on click, so the
                // trigger has to keep its click to itself.
                onClick={(event) => {
                    event.stopPropagation();
                    toggle();
                }}
                className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-text-charcoal-75 hover:tw-bg-charcoal-15"
            >
                <DotsIcon className="tw-h-3 tw-w-3" />
            </button>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        role="menu"
                        className="tw-min-w-[11rem] tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-1.5 tw-text-start tw-shadow-primary"
                    >
                        {items.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                role="menuitem"
                                disabled={!!action.disabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    close();
                                    action.onClick();
                                }}
                                className="tw-flex tw-w-full tw-items-center tw-gap-x-2 tw-rounded tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-charcoal hover:tw-bg-background disabled:tw-cursor-not-allowed disabled:tw-text-charcoal-50"
                            >
                                {action.icon}
                                <span>{action.label}</span>
                            </button>
                        ))}
                    </div>,
                    document.body,
                )}
        </>
    );
}
